"use client";
import { fblthpTotallyLostArtCrop } from "@/lib/backupCard";
import { calculatedDeckSize } from "@/lib/calculatedDeckSize";
import { ImageType } from "@/lib/getImageUrl";
import type { DeckResult } from "@/lib/queries/deckQueries";
import { cn } from "@/lib/utils";
import { colorSort } from "@cardheim/utils";
import { formatDistanceToNow } from "date-fns";
import Image from "next/image";
import Link from "next/link";
import { useImageContext } from "../ImageProvider/ImageContext";
import { MagicSymbols } from "../MagicSymbols";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardTitle,
} from "../ui/card";
import { DeckCardActions } from "./DeckCardActions";

interface Props {
	deck: DeckResult;
	asLink?: boolean;
	showActions?: boolean;
	className?: string;
}

export const DeckCard: React.FC<Props> = ({
	deck,
	asLink = true,
	showActions = false,
	className,
}) => {
	const { getImageUrl } = useImageContext();
	const sizes = calculatedDeckSize(deck.deck_size);

	const render = (
		<Card
			className={cn(
				"grid grid-cols-[80px_1fr] md:grid-cols-[160px_1fr]",
				"overflow-hidden w-full transition-all h-full",
				"shadow-xs hover:shadow-md hover:shadow-secondary",
				!asLink && "group",
				className,
			)}
		>
			<div className="relative w-full overflow-hidden">
				<Image
					alt={`Cover card for ${deck.name}`}
					width={160}
					height={118}
					src={
						deck.cover_card?.id
							? getImageUrl(deck.cover_card?.id, ImageType.ArtCrop)
							: fblthpTotallyLostArtCrop
					}
					className={cn(
						"pointer-events-none transition-all w-full h-full object-cover",
						showActions && "group-hover:scale-105",
					)}
				/>
				{showActions && <DeckCardActions deck={deck} />}
			</div>
			<div className="flex flex-col justify-between overflow-hidden">
				<CardContent className="flex flex-col p-2 overflow-hidden">
					<CardTitle className="truncate w-full">{deck.name}</CardTitle>
					<CardDescription className="w-full flex justify-between flex-wrap">
						<span>
							{deck.user_id === "wotc"
								? "Wizards of the Coast"
								: deck.author_name?.split(" ")[0]}
						</span>
						<span className="hidden md:block">
							{formatDistanceToNow(
								deck.updated_at || deck.created_at || new Date(1970),
								{ addSuffix: true, includeSeconds: false },
							)}
						</span>
					</CardDescription>
				</CardContent>
				<CardFooter className="mt-auto flex flex-wrap justify-between p-2">
					<div className="flex gap-1">
						<MagicSymbols
							symbols={
								deck.colors
									?.sort(colorSort)
									.map((c) => `{${c}}`)
									.join("") || "{C}"
							}
							size={16}
						/>
					</div>
					<div className="font-mono">
						{sizes.main} / {sizes.side}
					</div>
				</CardFooter>
			</div>
		</Card>
	);

	if (asLink) {
		return (
			<Link key={deck.id} href={`/decks/${deck.id}`} className="w-full group">
				{render}
			</Link>
		);
	}

	return render;
};
